import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import Antd, { message } from 'ant-design-vue'
import 'ant-design-vue/dist/reset.css'
import './assets/styles/main.css'
import { initViewport } from './utils/viewport'
import { initSafeArea, getSafeAreaTopPx, updateSafeAreaVars } from './utils/safeArea'
import DevicePlugin from './plugins/device'
import ResponsiveMixin from './mixins/responsive'
import ResponsiveDirective from './directives/responsive'
import { initPwaInstallListener } from './utils/pwaInstall'
import { ensureLatestVersionBeforeBoot, initAppVersionWatch } from './utils/appVersion'
import errorHandler from '@/utils/error-report/errorHandler.js'
import errorLogger from '@/utils/error-report/errorLogger.js'
import { initDeductionRules } from '@/utils/pointsDeductionRules'

initPwaInstallListener()
initSafeArea()

function applyMessageSafeTop () {
  message.config({ top: `${Math.max(20, getSafeAreaTopPx() + 8)}px` })
}

// 配置 message 全局参数（设置最高层级）
message.config({
  top: `${Math.max(20, getSafeAreaTopPx() + 8)}px`,
  duration: 3,
  maxCount: 2,
  getContainer: () => document.body,
  // 设置 z-index 为最高层级（高于所有弹窗）
  zIndex: 999999
})

window.addEventListener('orientationchange', applyMessageSafeTop)

// 屏蔽 ResizeObserver 错误（这是浏览器的已知问题，不影响功能）
// 方法1：捕获并忽略 ResizeObserver 错误
window.addEventListener('error', (e) => {
  if (e.message && (e.message.includes('ResizeObserver loop') || e.message.includes('ResizeObserver'))) {
    e.stopImmediatePropagation()
    e.preventDefault()
    
    // 隐藏 webpack-dev-server 的错误覆盖层
    const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div')
    const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay')
    if (resizeObserverErr) {
      resizeObserverErr.setAttribute('style', 'display: none !important')
    }
    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.setAttribute('style', 'display: none !important')
    }
  }
})

// 方法2：重写 ResizeObserver 的错误处理
const _ResizeObserver = window.ResizeObserver
window.ResizeObserver = class ResizeObserver extends _ResizeObserver {
  constructor(callback) {
    callback = ((entries, observer) => {
      window.requestAnimationFrame(() => {
        callback(entries, observer)
      })
    })
    super(callback)
  }
}

// 初始化视口工具（解决移动端vh/vw单位问题）
initViewport()

const app = createApp(App)

app.use(store)
app.use(router)
app.use(Antd)
app.use(DevicePlugin) // 注册设备检测插件
app.mixin(ResponsiveMixin) // 注册全局响应式 Mixin
app.directive('responsive', ResponsiveDirective) // 注册响应式指令

app.config.errorHandler = (err, instance, info) => {
  console.error('[VueError]', info, err)
  errorHandler.handleVueError(err, instance, info).catch(() => {})
}
app.config.globalProperties.$errorHandler = errorHandler
app.config.globalProperties.$errorLogger = errorLogger

// 启动时获取系统配置
async function initApp () {
  try {
    const reloading = await ensureLatestVersionBeforeBoot()
    if (reloading) return

    await store.dispatch('common/getSystemConf')
    const systemConf = store.getters['common/systemConf']

    // 获取动态菜单
    await store.dispatch('common/getMenu')

    // 每次启动/刷新：如已登录则刷新一次用户信息（含强制绑定手机号校验）
    // 这里不需要和登录流程“互斥”，user 模块内部会对 getUserInfo 做并发去重
    try {
      await store.dispatch('user/refreshUserInfoIfLoggedIn')
    } catch (e) {
      // 用户信息拉取失败不阻塞应用启动（路由守卫会在需要鉴权时兜底处理）
      console.warn('启动时刷新用户信息失败', e)
    }

    // 设置页面标题（启动时先设置站点标题 + 标语；具体页面名称由路由守卫补齐）
    if (typeof document !== 'undefined') {
      const siteTitle = systemConf?.web_site_title || '词呗AI'
      const slogan = systemConf?.web_site_slogan || ''
      document.title = slogan ? `${siteTitle} - ${slogan}` : siteTitle
    }

    // 预取积分扣费规则（失败不阻塞启动；提示文案为空则 UI 不展示）
    initDeductionRules()
  } catch (error) {
    console.error('初始化系统配置失败:', error)
  } finally {
    app.mount('#app')
    updateSafeAreaVars()
    applyMessageSafeTop()
    initAppVersionWatch()
    errorLogger.init().catch(() => {})
  }
}

initApp()
